import { useNavigate } from "react-router-dom";
import { ArrowRight, PenLine, HeartPulse, Sparkles, BookOpen, MessageCircleHeart, Users } from "lucide-react";
import MarketingLayout from "@/components/MarketingLayout";
import { useSeo } from "@/lib/seo";

const FEATURES = [
  {
    icon: PenLine,
    title: "Guided & voice journaling",
    body: "A private journal with gentle guided prompts when you want direction and a blank page when you don't. Prefer to talk it out? Voice journaling lets you speak your thoughts aloud and keeps them for you — private by design.",
    kw: "Guided journaling for women",
  },
  {
    icon: HeartPulse,
    title: "Daily emotional check-ins",
    body: "A few honest moments a day to notice how you're really doing. Each check-in reflects your mood back and points you to something that may help — a prompt, a calming tool, or a short lesson — so you can see patterns over time.",
    kw: "Daily emotional check-ins",
  },
  {
    icon: Sparkles,
    title: "Self-reflection & personalized reflections",
    body: "Thoughtful prompts and feedback that respond to what you've shared — never one-size-fits-all. Reflection that takes your life seriously and honors how culture, identity, family, and responsibility shape how each of us cares for ourselves.",
    kw: "Self-reflection tools for women",
  },
  {
    icon: BookOpen,
    title: "Learning library",
    body: "Short, practical lessons on confidence, boundaries, rest, relationships, motherhood, and more — alongside culturally responsive collections created around particular communities and experiences.",
    kw: "Wellness learning for women",
  },
  {
    icon: MessageCircleHeart,
    title: "AI wellness coach",
    body: "Warm, honest guidance whenever you need it — like a wise sister, not a clinician. The AI coach supports reflection and next steps. It is not a licensed therapist and is not a substitute for therapy, diagnosis, or crisis care.",
    kw: "Emotional wellness support",
  },
  {
    icon: Users,
    title: "Find a Therapist directory",
    body: "A free, public directory of licensed, culturally responsive therapists — welcoming women from all backgrounds, with deep experience supporting women of color. No account needed to browse.",
    kw: "Wellness support for women of color",
  },
];

export default function Features() {
  const navigate = useNavigate();
  useSeo({
    title: "Features — Guided Journaling, Check-Ins & More | Sisterhood+",
    description:
      "Explore Sisterhood+ features: guided and voice journaling, daily emotional check-ins, personalized reflections, a learning library, an AI wellness coach, and a therapist directory.",
    path: "/features",
  });

  return (
    <MarketingLayout>
      <section className="max-w-[1100px] mx-auto px-6 pt-6 pb-16">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.24em] text-[#C89B55] font-semibold mb-4">What's inside</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light leading-[1.05]">
            Everyday emotional wellness,<br /><span className="italic">built for the whole woman.</span>
          </h1>
          <p className="mt-6 text-[#291B2D]/70 text-lg leading-relaxed">
            Sisterhood+ brings together private tools for reflection, learning, and support — designed for what women share and thoughtful spaces for the stories that make us different.
          </p>
          <button onClick={() => navigate("/get-started")} data-testid="features-cta"
            className="mt-8 inline-flex items-center gap-2 bg-[#291B2D] text-white rounded-full px-8 py-4 font-medium hover:bg-[#352238] transition-smooth shadow-[0_10px_30px_rgba(41,27,45,0.25)]">
            Start your 7-day free trial <ArrowRight size={16} />
          </button>
        </div>

        <div className="mt-14 grid gap-5 md:grid-cols-2">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="bg-white rounded-3xl p-8 card-soft">
                <div className="w-11 h-11 rounded-2xl bg-[#F7F2EA] flex items-center justify-center text-[#C89B55] mb-5">
                  <Icon size={20} />
                </div>
                <h2 className="font-heading text-2xl mb-2">{f.title}</h2>
                <p className="text-[15px] text-[#291B2D]/70 leading-[1.7]">{f.body}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-14 bg-[#291B2D] text-white rounded-[2rem] p-10 text-center">
          <h2 className="font-heading text-3xl font-light">Your growth. Your peace. Your future.</h2>
          <p className="text-white/70 mt-3 max-w-xl mx-auto">Start free. No judgment. Just a private space to become more of who you already are.</p>
          <button onClick={() => navigate("/get-started")} data-testid="features-cta-bottom"
            className="mt-7 inline-flex items-center gap-2 bg-[#C89B55] text-[#291B2D] rounded-full px-8 py-4 font-semibold hover:bg-[#B38743] transition-smooth">
            Start your 7-day free trial <ArrowRight size={16} />
          </button>
        </div>
      </section>
    </MarketingLayout>
  );
}
